import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import useEstimatedArrivalText from '#/transit-tracker/hooks/useEstimatedArrivalText';
import { getStopFromArrival } from '#/transit-tracker/utils/arrivals';
import useVehicleCentering from '#/transit-tracker/hooks/useVehicleCentering';
import {
  setFocusedOnHighlightedStop,
  setHighlightedStop,
} from '#/transit-tracker/slice';
import GTM_HANDLES from '#/shared/gtm-handles';
import * as Styled from './styled';

function VehicleStopsList({
  stops,
  selectedStopId,
  nextArrivalStopId,
  navigateToMap,
  highlightStop,
  isFavoriteStop,
}) {
  const dispatch = useDispatch();
  const { centerVehicleOnMap } = useVehicleCentering();

  const { getEstimatedArrivalText } = useEstimatedArrivalText();
  const nextArrivalIx = stops.findIndex(s => s.stopId === nextArrivalStopId);
  useEffect(() => {
    dispatch(setHighlightedStop(null));
    dispatch(setFocusedOnHighlightedStop(false));
  }, [dispatch]);
  useEffect(() => {
    centerVehicleOnMap(true);
  }, [centerVehicleOnMap]);

  const intl = useIntl();

  return (
    <Styled.StopsList>
      {stops.map((s, ix) => {
        const StopItem =
          String(s.stopId) === String(selectedStopId)
            ? Styled.SelectedStopItem
            : Styled.StopItem;

        let arrivalText;
        let accessibilityIntlId;

        if (s.status === 'estimated') {
          arrivalText = getEstimatedArrivalText(s).arrivalText;
          accessibilityIntlId = s.showMilesAway
            ? 'distance_acc'
            : 'estimated-time_acc';
        }

        return (
          // Ordinarily, we shouldn't put click handlers on non-interactive elements like StopItem.
          // Here though the click event on the child button bubbles up to the click handler.
          // So it still works accessibly, and lets us retain the formatting we want while
          // conveniently allowing users to zoom the map by clicking anywhere within StopItem
          <StopItem
            key={s.stopId}
            onDoubleClick={() => navigateToMap(s.stopId)}
            onClick={() => {
              dispatch(setFocusedOnHighlightedStop(false));
              centerVehicleOnMap(false);
              highlightStop({
                ...getStopFromArrival(s),
                isFavorite: isFavoriteStop(s),
                centerOnMap: true,
              });
            }}
          >
            {accessibilityIntlId && (
              <ScreenReaderText>
                <FormattedMessage id={`arrivals.${accessibilityIntlId}`} />
              </ScreenReaderText>
            )}
            <Styled.ArrivalText>
              <Styled.ArrivalSpan>{arrivalText}</Styled.ArrivalSpan>
            </Styled.ArrivalText>

            <Styled.StopNameWrapper vehiclePassedStop={nextArrivalIx > ix}>
              <Styled.StopIconColumn>
                <Styled.StopIcon
                  title={intl.formatMessage({
                    id: 'vehicle-tracking.stop-icon-title',
                  })}
                  role="presentation"
                />
              </Styled.StopIconColumn>
              <Styled.MapButton
                data-gtm={GTM_HANDLES.vehicle.vehicleStopMapButton}
              >
                {s.stopName}
                {s.dropOffOnly && (
                  <Styled.DropOffOnlyText>
                    <FormattedMessage id="detailview.drop-off-only" />
                  </Styled.DropOffOnlyText>
                )}
              </Styled.MapButton>
            </Styled.StopNameWrapper>
            <Styled.MapIcon
              title={intl.formatMessage({
                id: 'vehicle-tracking.map-icon-title',
              })}
            />
          </StopItem>
        );
      })}
      {stops.length === 0 && (
        <Styled.NoStops>
          <FormattedMessage id="detailview.vehiclenostops" />
        </Styled.NoStops>
      )}
    </Styled.StopsList>
  );
}

VehicleStopsList.propTypes = {
  stops: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFavoriteStop: PropTypes.func.isRequired,
  selectedStopId: PropTypes.string,
  /** ID for the stop at which the vehicle will next arrive */
  nextArrivalStopId: PropTypes.number,
  navigateToMap: PropTypes.func,
  /** Propagates the stop so it becomes highlighted the map view */
  highlightStop: PropTypes.func,
};

VehicleStopsList.defaultProps = {
  selectedStopId: null,
  nextArrivalStopId: null,
  navigateToMap: () => {},
  highlightStop: () => {},
};

export default VehicleStopsList;
